import { createSlice } from '@reduxjs/toolkit';

import { JSON_QUERY_DEFAULT } from '../../constants/jsonQuery';
import { SEARCH_META_DEFAULT } from '../../constants/searchMeta';
import {
  open,
  requestSuggestions,
  selectCity,
  selectCitySuggestion,
  selectRegion,
  updateMeta,
} from '../../thunks/locationPopup';

import { type ILocationPopupState } from './types';

const locationPopupInitialState: ILocationPopupState = {
  status: 'initial',
  isOpen: false,
  cities: undefined,
  selectedRegion: undefined,
  selectedCity: undefined,
  jsonQuery: JSON_QUERY_DEFAULT,
  meta: {
    status: 'initial',
    data: SEARCH_META_DEFAULT,
  },
  suggest: {
    status: 'initial',
    value: [],
  },
};

export const locationPopupSlice = createSlice({
  initialState: locationPopupInitialState,
  name: 'locationPopup',
  reducers: {
    close(state) {
      state.isOpen = false;
    },
    selectCountry(state) {
      state.isOpen = true;
      state.selectedRegion = undefined;
      state.selectedCity = undefined;
      state.cities = undefined;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(open.pending, state => {
        state.status = 'loading';
        state.isOpen = true;
        state.cities = undefined;
        state.selectedCity = undefined;
        state.selectedRegion = undefined;
        state.meta = {
          status: 'initial',
          data: SEARCH_META_DEFAULT,
        };
        state.suggest = {
          status: 'initial',
          value: [],
        };
        state.jsonQuery = JSON_QUERY_DEFAULT;
      })
      .addCase(open.fulfilled, (state, action) => {
        state.status = 'succeed';
        state.selectedRegion = action.payload.selectedRegion;
        state.selectedCity = action.payload.selectedCity;
        state.cities = action.payload.cities;
        state.jsonQuery = action.payload.jsonQuery;
      })
      .addCase(open.rejected, state => {
        state.status = 'failed';
      })
      .addCase(selectCity.fulfilled, (state, action) => {
        state.selectedCity = action.payload.city;
        state.jsonQuery = action.payload.jsonQuery;
      })
      .addCase(updateMeta.pending, state => {
        state.meta.status = 'loading';
      })
      .addCase(updateMeta.fulfilled, (state, action) => {
        state.meta.status = 'succeed';
        state.meta.data = action.payload;
      })
      .addCase(updateMeta.rejected, (state, action) => {
        if (action.payload === 'canceled') {
          return;
        }

        state.meta.status = 'failed';
      })
      .addCase(selectRegion.pending, state => {
        state.status = 'loading';
      })
      .addCase(selectRegion.fulfilled, (state, action) => {
        state.status = 'succeed';
        state.cities = action.payload.cities;
        state.jsonQuery = action.payload.jsonQuery;
        state.selectedRegion = action.payload.region;
        state.selectedCity = undefined;
      })
      .addCase(selectRegion.rejected, state => {
        state.status = 'failed';
      })
      .addCase(requestSuggestions.pending, state => {
        state.suggest.status = 'loading';
      })
      .addCase(requestSuggestions.fulfilled, (state, action) => {
        state.suggest.status = 'succeed';
        state.suggest.value = action.payload;
      })
      .addCase(requestSuggestions.rejected, state => {
        state.status = 'failed';
        state.suggest.value = [];
      })
      .addCase(selectCitySuggestion.pending, state => {
        state.status = 'loading';
      })
      .addCase(selectCitySuggestion.fulfilled, (state, action) => {
        state.status = 'succeed';
        state.cities = action.payload.cities;
        state.selectedCity = action.payload.selectedCity;
        state.selectedRegion = action.payload.selectedRegion;
        state.jsonQuery = action.payload.jsonQuery;
      })
      .addCase(selectCitySuggestion.rejected, state => {
        state.status = 'failed';
      });
  },
});

export const { reducer: locationPopupReducer, actions: locationPopupActions } = locationPopupSlice;
